import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Company } from 'app/models/company.models';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomerRule, CustomerRuleUpdate } from 'app/models/customer-rule';
import { ApiService } from 'app/services/api.service';
import { TranslateModule } from '@ngx-translate/core';
import { CustomerCallListUpdate } from 'app/models/call-list-item';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

interface AreaUI {
    area: string;
    rows: {
      callListId?: number;
      phoneNumber: string;
      timeRange: string;
    }[];
  }

@Component({
    selector: 'app-company-emaac-info',
    templateUrl: './company-emaac-info.component.html',
    styleUrls: ['./company-emaac-info.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    TranslateModule,
    FormsModule
]
})
export class CompanyEmaacInfoComponent implements OnChanges {
    constructor(private api: ApiService) {}

    @Input() company!: Company;
    rules: CustomerRule[] = [];
    areas: AreaUI[] = [];
    loading = true;

    ngOnChanges(changes: SimpleChanges) {
      const company = changes['company']?.currentValue;
    
      if (!company?.companyId) return;
    
      this.loadData(company.companyId);
    }
  
    addRule() {
     this.rules.push({
        ruleId: undefined,
        subject: '',
        description: ''
     });
    }

    addArea() {
        this.areas.push({
          area: '',
          rows: [
            {
              phoneNumber: '',
              timeRange: ''
            }
          ]
        });
      }

      addPhoneRow(areaIndex: number) {
        this.areas[areaIndex].rows.push({
          phoneNumber: '',
          timeRange: ''
        });
      }
  
    save() {
        const body: CustomerRuleUpdate = {
          addOrUpdate: [],
          deleteRuleIds: []
        };
      
        for (const rule of this.rules) {
          const hasContent =
            rule.subject?.trim() && rule.description?.trim();
      
          if (hasContent) {
            body.addOrUpdate.push({
              ruleId: rule.ruleId,
              subject: rule.subject,
              description: rule.description
            });
          } else if (rule.ruleId) {
            body.deleteRuleIds.push(rule.ruleId);
          }
        }
      
        this.api
          .saveCustomerRules(this.company.companyId, body)
          .subscribe(() => {
            this.loadData(this.company.companyId);
          });
      }

      saveCallListItems() {
        const body: CustomerCallListUpdate = {
          addOrUpdate: [],
          deleteItemIds: []
        };
      
        for (const area of this.areas) {
      
          for (const row of area.rows) {
      
            const parsed = this.parseTimeRange(row.timeRange);
            const normalizedPhone = this.normalizePhone(row.phoneNumber);

            const hasContent =
            area.area?.trim() &&
            row.phoneNumber?.trim() &&
            this.isValidPhone(row.phoneNumber) &&
            parsed.start &&
            parsed.end;

            if (!hasContent) {
            if (row.callListId) {
                body.deleteItemIds.push(row.callListId);
            }
            continue;
            }

            body.addOrUpdate.push({
            callListId: row.callListId,
            customerId: this.company.companyId,
            area: area.area,
            phoneNumber: normalizedPhone,
            callWindowStart: parsed.start,
            callWindowEnd: parsed.end
            });
          }
        }
      
        this.api.saveCustomerCallList(this.company.companyId, body)
          .subscribe(() => {
            this.loadData(this.company.companyId);
          });
      }

      private loadData(companyId: string) {
        this.loading = true;
        this.rules = [];
        this.areas = [];
        forkJoin({
          rules: this.api.getCompanyRules(companyId, this.company.server),
          callList: this.api.getCustomerCallList(companyId, this.company.server)
        })
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (res) => {
            this.rules = res.rules;
      
            const grouped: { [key: string]: AreaUI } = {};
      
            for (const item of res.callList) {
              if (!grouped[item.area]) {
                grouped[item.area] = {
                  area: item.area,
                  rows: []
                };
              }
      
              grouped[item.area].rows.push({
                callListId: item.callListId,
                phoneNumber: item.phoneNumber,
                timeRange: this.formatTimeRange(
                  item.callWindowStart,
                  item.callWindowEnd
                )
              });
            }
      
            this.areas = Object.values(grouped);
          },
          error: (err) => {
            console.error('EMAAC load failed', err);
            this.rules = [];
            this.areas = [];
          }
        });
      }

      private normalizeTime(time: string): string {
        if (!time) return time;
        return time.length === 5 ? `${time}:00` : time;
      }

      private parseTimeRange(range: string): { start: string; end: string } {
        if (!range) return { start: '', end: '' };
      
        const parts = range.split('-');
      
        return {
          start: this.normalizeTime(parts[0]?.trim()),
          end: this.normalizeTime(parts[1]?.trim())
        };
      }
      private formatTimeRange(start: string, end: string): string {
        return `${start?.substring(0, 5)}-${end?.substring(0, 5)}`;
      }

      private normalizePhone(input: string): string {
        if (!input) return '';

        let phone = input.replace(/[\s()-]/g, '');

        if (phone.startsWith('00')) {
            phone = '+' + phone.slice(2);
        }
        if (phone.startsWith('0')) {
            phone = '+358' + phone.slice(1);
        }

        return phone;
        }

    isValidPhone(input: string): boolean {
        if (!input?.trim()) return true;
          
        const phone = this.normalizePhone(input);
          
        if (!phone.startsWith('+')) return false;
        if (!/^\+[0-9]+$/.test(phone)) return false;
          
        const digits = phone.replace('+', '');
          
        return digits.length >= 10 && digits.length <= 15;
    }

    isCallListValid(): boolean {
      return this.areas.every(area =>
      area.rows.every(row => this.isValidPhone(row.phoneNumber))
      );
  }
}